<template>
    <div class="examination-form__eye">
        <!-- //////////////////////////////////////// Common Fields -->

        <template v-if="formData.lens_type && isReorder">
            <!-- Lens Serial Number -->
            <div class="examination-form__field">
                <label :for="id + '-serial_number'" class="examination-form__label">
                    {{ $t('parameters.LENS_SERIAL_NUMBER.label') }}
                </label>

                <input
                    :id="id + '-serial_number'"
                    v-model="formData.serial_number"
                    type="text"
                    class="examination-form__input"
                    @input="emitFormData"
                >

                <div class="examination-form__field-help">
                    {{ $t('form.help.previous_serial_number') }}
                </div>
            </div>
        </template>

        <!-- Lens Type -->
        <div class="examination-form__field">
            <label class="examination-form__label">
                {{ $t('parameters.LENS_TYPE.label') }}

                <span class="text-red-400">*</span>
            </label>

            <topo-radio
                v-model="formData.lens_type"
                :options="lensTypeOptions"
                @update:model-value="lensTypeChanged"
            />
        </div>

        <template v-if="formData.lens_type">
            <!-- Prototype -->
            <div v-if="hasPrototype" class="examination-form__field">
                <label class="examination-form__label">
                    {{ $t('parameters.LENS_PROTOTYPE.label') }}
                </label>

                <topo-radio
                    v-model="formData.prototype"
                    :options="lensPrototypeOptions"
                />
            </div>

            <!-- Focal -->
            <div v-if="focalOptions.length" class="examination-form__field">
                <label class="examination-form__label">
                    {{ $t('parameters.FOCAL.label') }}
                </label>

                <topo-radio
                    v-model="formData.focal"
                    :options="focalOptions"
                />
            </div>

            <!-- Variant -->
            <div v-if="formData.lens_type === 'ORTHOK'" class="examination-form__field">
                <label class="examination-form__label">
                    {{ $t('parameters.LENS_VARIANT.label') }}
                </label>

                <topo-radio
                    v-model="formData.variant"
                    :options="variantOptions"
                />
            </div>

            <!-- Renewal Period -->
            <div class="examination-form__field">
                <label class="examination-form__label">
                    {{ $t('parameters.RENEWAL_PERIOD.label') }}
                </label>

                <topo-radio
                    v-model="formData.renewal_period"
                    :options="renewalPeriodOptions"
                />
            </div>

            <!-- Return Option -->
            <div class="examination-form__field">
                <label class="examination-form__label">
                    {{ $t('parameters.RETURN_OPTION.label') }}
                </label>

                <topo-radio
                    v-model="formData.return_option"
                    :options="returnOptions"
                />
            </div>

            <!-- Scleral Trial Lens -->
            <div v-if="formData.lens_type === 'SCLERAL'" class="examination-form__field">
                <label class="examination-form__label" :for="id + '-trial_lens'">
                    {{ $t('parameters.TRIAL_LENS.label') }}
                </label>

                <select
                    :id="id + '-trial_lens'"
                    v-model="formData.trial_lens"
                    class="examination-form__input"
                    @input="emitFormData"
                >
                    <option
                        v-for="(trialLens, index) in trialLensOptions"
                        :key="index"
                        :value="trialLens"
                    >
                        {{ trialLens }}
                    </option>
                </select>
            </div>


            <!-- //////////////////////////////////////// Eye & Lens Fields -->

            <template v-if="formData.lens_type !== 'SCLERAL'">
                <!-- Sphere -->
                <div class="examination-form__field examination-form__field--number">
                    <label :for="id + '-sphere'" class="examination-form__label">
                        {{ $t('parameters.LENS_SPHERE.label') }}
                    </label>

                    <template v-if="formData.lens_type !== 'ORTHOK'">
                        <input
                            :id="id + '-sphere'"
                            v-model="formData.sphere"
                            type="text"
                            class="examination-form__input"
                            @input="emitFormData"
                        >

                        <div class="examination-form__field-help">
                            {{ $t('form.help.value_between', { min: '-40.00', max: '+40.00' }) }}
                        </div>
                    </template>

                    <template v-else>
                        <input
                            :id="id + '-sphere'"
                            v-model="formData.sphere"
                            type="text"
                            class="examination-form__input"
                            @input="emitFormData"
                        >

                        <div class="examination-form__field-help">
                            {{ $t('form.help.value_between', { min: '-9.00', max: '+5.00' }) }}
                        </div>
                    </template>
                </div>

                <!-- Cylinder -->
                <div class="examination-form__field examination-form__field--number">
                    <label :for="id + '-cylinder'" class="examination-form__label">
                        {{ $t('parameters.LENS_CYLINDER.label') }}
                    </label>

                    <template v-if="formData.lens_type !== 'ORTHOK'">
                        <input
                            :id="id + '-cylinder'"
                            v-model="formData.cylinder"
                            type="text"
                            class="examination-form__input"
                            @input="emitFormData"
                        >

                        <div class="examination-form__field-help">
                            {{ $t('form.help.value_between', { min: '-15.00', max: '-0.01' }) }}
                        </div>
                    </template>

                    <template v-else>
                        <input
                            :id="id + '-cylinder'"
                            v-model="formData.cylinder"
                            type="text"
                            class="examination-form__input"
                            @input="emitFormData"
                        >

                        <div class="examination-form__field-help">
                            {{ $t('form.help.value_between', { min: '-6.00', max: '-0.01' }) }}
                        </div>
                    </template>
                </div>

                <!-- Cylinder Axis -->
                <div class="examination-form__field examination-form__field--number">
                    <label :for="id + '-cylinder_axis'" class="examination-form__label">
                        {{ $t('parameters.LENS_CYLINDER_AXIS.label') }}
                    </label>

                    <input
                        :id="id + '-cylinder_axis'"
                        v-model="formData.cylinder_axis"
                        type="text"
                        class="examination-form__input"
                        @input="emitFormData"
                    >

                    <div class="examination-form__field-help">
                        {{ $t('form.help.value_between', { min: '0°', max: '180°' }) }}
                    </div>
                </div>

                <!-- Addition -->
                <div v-if="formData.focal !== 'UNIFOCAL'" class="examination-form__field examination-form__field--number">
                    <label :for="id + '-addition'" class="examination-form__label">
                        {{ $t('parameters.LENS_ADDITION.label') }}
                    </label>

                    <input
                        :id="id + '-addition'"
                        v-model="formData.addition"
                        type="text"
                        class="examination-form__input"
                        @input="emitFormData"
                    >

                    <div class="examination-form__field-help">
                        {{ $t('form.help.value_between', { min: '+0.50', max: '+4.00' }) }}
                    </div>
                </div>

                <!-- Guiding Eye -->
                <div class="examination-form__field">
                    <label class="examination-form__label">
                        {{ $t('form.GUIDING_EYE.label') }}
                    </label>

                    <topo-radio
                        v-model="formData.guiding_eye"
                        :options="guidingEyeOptions"
                    />
                </div>

                <!-- Acuity -->
                <div class="examination-form__field examination-form__field--number">
                    <label :for="id + '-acuity'" class="examination-form__label">
                        {{ $t('parameters.ACUITY.label') }}
                    </label>

                    <input
                        :id="id + '-acuity'"
                        v-model="formData.acuity"
                        type="text"
                        class="examination-form__input"
                        @input="emitFormData"
                    >

                    <div class="examination-form__field-help">
                        {{ $t('form.help.value_between', { min: '0.05', max: '2.00' }) }}
                    </div>
                </div>

                <!-- Vertex Distance -->
                <div class="examination-form__field examination-form__field--number">
                    <label :for="id + '-vertex_distance'" class="examination-form__label">
                        {{ $t('parameters.VERTEX_DISTANCE.label') }}
                    </label>

                    <input
                        :id="id + '-vertex_distance'"
                        v-model="formData.vertex_distance"
                        type="text"
                        class="examination-form__input"
                        @input="emitFormData"
                    >

                    <div class="examination-form__field-help">
                        {{ $t('form.help.value_between', { min: '0', max: '25' }) }}
                    </div>
                </div>
            </template>


            <!-- //////////////////////////////////////// Over-Refraction Fields -->

            <template v-if="(formData.lens_type && isReorder) || formData.lens_type === 'SCLERAL'">
                <!-- //////////////////// Over-Refraction with Lens -->

                <div class="examination-form__sub-heading">
                    {{ $t('form.heading.over_refraction_with_lens') }}
                </div>

                <!-- Sphere -->
                <div class="examination-form__field examination-form__field--number">
                    <label :for="id + '-rorl_sphere'" class="examination-form__label">
                        {{ $t('parameters.LENS_SPHERE.label') }}
                    </label>

                    <template v-if="formData.lens_type !== 'ORTHOK'">
                        <input
                            :id="id + '-rorl_sphere'"
                            v-model="formData.rorl_sphere"
                            type="text"
                            class="examination-form__input"
                            @input="emitFormData"
                        >

                        <div class="examination-form__field-help">
                            {{ $t('form.help.value_between', { min: '-40.00', max: '+40.00' }) }}
                        </div>
                    </template>

                    <template v-else>
                        <input
                            :id="id + '-rorl_sphere'"
                            v-model="formData.rorl_sphere"
                            type="text"
                            class="examination-form__input"
                            @input="emitFormData"
                        >

                        <div class="examination-form__field-help">
                            {{ $t('form.help.value_between', { min: '-9.00', max: '+5.00' }) }}
                        </div>
                    </template>
                </div>

                <!-- Cylinder -->
                <div class="examination-form__field examination-form__field--number">
                    <label :for="id + '-rorl_cylinder'" class="examination-form__label">
                        {{ $t('parameters.LENS_CYLINDER.label') }}
                    </label>

                    <template v-if="formData.lens_type !== 'ORTHOK'">
                        <input
                            :id="id + '-rorl_cylinder'"
                            v-model="formData.rorl_cylinder"
                            type="text"
                            class="examination-form__input"
                            @input="emitFormData"
                        >

                        <div class="examination-form__field-help">
                            {{ $t('form.help.value_between', { min: '-15.00', max: '-0.01' }) }}
                        </div>
                    </template>

                    <template v-else>
                        <input
                            :id="id + '-rorl_cylinder'"
                            v-model="formData.rorl_cylinder"
                            type="text"
                            class="examination-form__input"
                            @input="emitFormData"
                        >

                        <div class="examination-form__field-help">
                            {{ $t('form.help.value_between', { min: '-6.00', max: '-0.01' }) }}
                        </div>
                    </template>
                </div>

                <!-- Cylinder Axis -->
                <div class="examination-form__field examination-form__field--number">
                    <label :for="id + '-rorl_cylinder_axis'" class="examination-form__label">
                        {{ $t('parameters.LENS_CYLINDER_AXIS.label') }}
                    </label>

                    <input
                        :id="id + '-rorl_cylinder_axis'"
                        v-model="formData.rorl_cylinder_axis"
                        type="text"
                        class="examination-form__input"
                        @input="emitFormData"
                    >

                    <div class="examination-form__field-help">
                        {{ $t('form.help.value_between', { min: '0°', max: '180°' }) }}
                    </div>
                </div>

                <!-- Addition -->
                <div class="examination-form__field examination-form__field--number">
                    <label :for="id + '-rorl_addition'" class="examination-form__label">
                        {{ $t('parameters.LENS_ADDITION.label') }}
                    </label>

                    <input
                        :id="id + '-rorl_addition'"
                        v-model="formData.rorl_addition"
                        type="text"
                        class="examination-form__input"
                        @input="emitFormData"
                    >

                    <div class="examination-form__field-help">
                        {{ $t('form.help.value_between', { min: '+0.50', max: '+4.00' }) }}
                    </div>
                </div>

                <!-- Stabilization -->
                <div class="examination-form__field examination-form__field--number">
                    <label :for="id + '-rorl_stabilization'" class="examination-form__label">
                        {{ $t('parameters.STABILIZATION.label') }}
                    </label>

                    <input
                        :id="id + '-rorl_stabilization'"
                        v-model="formData.rorl_stabilization"
                        type="text"
                        class="examination-form__input"
                        @input="emitFormData"
                    >

                    <div class="examination-form__field-help">
                        {{ $t('form.help.value_between', { min: '0°', max: '180°' }) }}
                    </div>
                </div>

                <!-- Visus with O/Rx -->
                <div class="examination-form__field examination-form__field--number">
                    <label :for="id + '-rorl_visus_orx'" class="examination-form__label">
                        {{ $t('parameters.VISUS_WITH_ORX.label') }}
                    </label>

                    <input
                        :id="id + '-rorl_visus_orx'"
                        v-model="formData.rorl_visus_orx"
                        type="text"
                        class="examination-form__input"
                        @input="emitFormData"
                    >

                    <div class="examination-form__field-help">
                        {{ $t('form.help.value_between', { min: '+0.05', max: '+2.00' }) }}
                    </div>
                </div>


                <!-- //////////////////// Over-Refraction without Lens -->

                <template v-if="formData.lens_type === 'ORTHOK'">
                    <div class="examination-form__sub-heading">
                        {{ $t('form.heading.over_refraction_without_lens') }}
                    </div>

                    <!-- Sphere -->
                    <div class="examination-form__field examination-form__field--number">
                        <label :for="id + '-ror_sphere'" class="examination-form__label">
                            {{ $t('parameters.LENS_SPHERE.label') }}
                        </label>

                        <input
                            :id="id + '-ror_sphere'"
                            v-model="formData.ror_sphere"
                            type="text"
                            class="examination-form__input"
                            @input="emitFormData"
                        >

                        <div class="examination-form__field-help">
                            {{ $t('form.help.value_between', { min: '-40.00', max: '+40.00' }) }}
                        </div>
                    </div>

                    <!-- Cylinder -->
                    <div class="examination-form__field examination-form__field--number">
                        <label :for="id + '-ror_cylinder'" class="examination-form__label">
                            {{ $t('parameters.LENS_CYLINDER.label') }}
                        </label>

                        <input
                            :id="id + '-ror_cylinder'"
                            v-model="formData.ror_cylinder"
                            type="text"
                            class="examination-form__input"
                            @input="emitFormData"
                        >

                        <div class="examination-form__field-help">
                            {{ $t('form.help.value_between', { min: '-15.00', max: '-0.01' }) }}
                        </div>
                    </div>

                    <!-- Cylinder Axis -->
                    <div class="examination-form__field examination-form__field--number">
                        <label :for="id + '-ror_cylinder_axis'" class="examination-form__label">
                            {{ $t('parameters.LENS_CYLINDER_AXIS.label') }}
                        </label>

                        <input
                            :id="id + '-ror_cylinder_axis'"
                            v-model="formData.ror_cylinder_axis"
                            type="text"
                            class="examination-form__input"
                            @input="emitFormData"
                        >

                        <div class="examination-form__field-help">
                            {{ $t('form.help.value_between', { min: '0°', max: '180°' }) }}
                        </div>
                    </div>

                    <!-- Addition -->
                    <div class="examination-form__field examination-form__field--number">
                        <label :for="id + '-ror_addition'" class="examination-form__label">
                            {{ $t('parameters.LENS_ADDITION.label') }}
                        </label>

                        <input
                            :id="id + '-ror_addition'"
                            v-model="formData.ror_addition"
                            type="text"
                            class="examination-form__input"
                            @input="emitFormData"
                        >

                        <div class="examination-form__field-help">
                            {{ $t('form.help.value_between', { min: '+0.50', max: '+4.00' }) }}
                        </div>
                    </div>

                    <!-- Visus with O/Rx -->
                    <div class="examination-form__field examination-form__field--number">
                        <label :for="id + '-ror_visus_orx'" class="examination-form__label">
                            {{ $t('parameters.VISUS_WITH_ORX.label') }}
                        </label>

                        <input
                            :id="id + '-ror_visus_orx'"
                            v-model="formData.ror_visus_orx"
                            type="text"
                            class="examination-form__input"
                            @input="emitFormData"
                        >

                        <div class="examination-form__field-help">
                            {{ $t('form.help.value_between', { min: '+0.05', max: '+2.00' }) }}
                        </div>
                    </div>
                </template>
            </template>


            <!-- //////////////////////////////////////// Material -->

            <div class="examination-form__field">
                <label :for="id + '-material'" class="examination-form__label">
                    {{ $t('parameters.LENS_MATERIAL.label') }}
                </label>

                <select
                    :id="id + '-material'"
                    v-model="formData.material"
                    class="examination-form__input"
                    @input="emitFormData"
                >
                    <option
                        v-for="(material, index) in materialOptions"
                        :key="index"
                        :value="material.toUpperCase().replace(/ /g, '_')"
                    >
                        {{ material }}
                    </option>
                </select>
            </div>


            <!-- //////////////////////////////////////// Other General Fields -->

            <!-- Remark -->
            <div class="examination-form__field">
                <label :for="id + '-remark'" class="examination-form__label">
                    {{ $t('parameters.LENS_COMMENT.label') }}
                </label>

                <textarea
                    :id="id + '-remark'"
                    v-model="formData.remark"
                    rows="3"
                    class="examination-form__input"
                ></textarea>
            </div>
        </template>
    </div>
</template>

<script>
import Numbers from '@/utils/Numbers';
import Strings from '@/utils/Strings';

const RENEWAL_PERIODS = {
    SOFT:    [3, 6, 12],
    RGP:     [12],
    SCLERAL: [12],
    ORTHOK:  [12],
};

export default
{
    name: 'EyeForm',

    components:
    {
    },

    props:
    {
        modelValue:
        {
            type:     Object,
            required: true,
        },

        isReorder:
        {
            type:    Boolean,
            default: false,
        },
    },

    emits: ['update:modelValue'],

    data()
    {
        return {
            id:       Numbers.randomHex(),
            formData: {},
        };
    },

    computed:
    {
        lensTypeOptions()
        {
            return [
                {
                    label: this.$t('parameters.LENS_TYPE.values.SOFT.label'),
                    value: 'SOFT',
                },
                {
                    label: this.$t('parameters.LENS_TYPE.values.RGP.label'),
                    value: 'RGP',
                },
                {
                    label: this.$t('parameters.LENS_TYPE.values.SCLERAL.label'),
                    value: 'SCLERAL',
                },
                {
                    label: this.$t('parameters.LENS_TYPE.values.ORTHOK.label'),
                    value: 'ORTHOK',
                },
            ];
        },

        lensPrototypeOptions()
        {
            switch (this.formData.lens_type)
            {
                case 'SOFT':
                    return [
                        {
                            label: this.$t('parameters.LENS_PROTOTYPE.values.REGULAR.label'),
                            value: 'REGULAR',
                        },
                        {
                            label: this.$t('parameters.LENS_PROTOTYPE.values.HYDROCONE.label'),
                            value: 'HYDROCONE',
                        },
                        {
                            label: this.$t('parameters.LENS_PROTOTYPE.values.HYDROMED.label'),
                            value: 'HYDROMED',
                        },
                    ];

                case 'RGP':
                    return [
                        {
                            label: this.$t('parameters.LENS_PROTOTYPE.values.REGULAR.label'),
                            value: 'REGULAR',
                        },
                        {
                            label: this.$t('parameters.LENS_PROTOTYPE.values.FLEXCONE.label'),
                            value: 'FLEXCONE',
                        },
                        {
                            label: this.$t('parameters.LENS_PROTOTYPE.values.FLEXMED.label'),
                            value: 'FLEXMED',
                        },
                    ];

                default:
                    throw new Error(`Lens type ${this.formData.lens_type} does not have prototypes`);
            }
        },

        renewalPeriodOptions()
        {
            return RENEWAL_PERIODS[this.formData.lens_type]
                .map(ro => ({
                    label: this.$t('parameters.RENEWAL_PERIOD.values.MONTHS.label', ro),
                    value: ro,
                }));
        },

        returnOptions()
        {
            return [
                {
                    label: this.$t('parameters.RETURN_OPTION.values.SIMPLE.label'),
                    value: 'SIMPLE',
                },
                {
                    label: this.$t('parameters.RETURN_OPTION.values.CLASSIC.label'),
                    value: 'CLASSIC',
                },
                {
                    label: this.$t('parameters.RETURN_OPTION.values.CLASSIC_PLUS.label'),
                    value: 'CLASSIC_PLUS',
                },
            ];
        },

        trialLensOptions()
        {
            return [
                'S 1 (3.500)',
                'S 2 (3.625)',
                'S 3 (3.750)',
                'M 1 (3.875)',
                'M 2 (4.000)',
                'M 3 (4.125)',
                'L 1 (4.250)',
                'L 2 (4.375)',
                'L 3 (4.500)',
                'XL 1 (4.625)',
                'XL 2 (4.750)',
                'XL 3 (4.875)',
            ];
        },

        focalOptions()
        {
            const focalOptions = [];

            if (this.hasUnifocal)
            {
                focalOptions.push({
                    label: this.$t('parameters.FOCAL.values.UNIFOCAL.label'),
                    value: 'UNIFOCAL',
                });
            }

            if (this.hasMultifocal)
            {
                focalOptions.push({
                    label: this.$t('parameters.FOCAL.values.MULTIFOCAL.label'),
                    value: 'MULTIFOCAL',
                });
            }

            // hack: Add the "Myopia Management" variant to the "Focal" field
            if (this.hasMyopiaManagement)
            {
                focalOptions.push({
                    label: this.$t('parameters.LENS_VARIANT.values.MYOPIA_MANAGEMENT.label'),
                    value: 'MYOPIA_MANAGEMENT',
                });
            }

            return focalOptions;
        },

        variantOptions()
        {
            return [
                {
                    label: this.$t('parameters.LENS_VARIANT.values.MYOPIA.label'),
                    label: 'Myopia',
                },
                {
                    label: this.$t('parameters.LENS_VARIANT.values.HYPEROPIA.label'),
                    value: 'HYPEROPIA',
                },
                {
                    label: this.$t('parameters.LENS_VARIANT.values.MYOPIA_MANAGEMENT.label'),
                    value: 'MYOPIA_MANAGEMENT',
                },
                {
                    label: this.$t('parameters.LENS_VARIANT.values.MYOPIA_FOR_PRESBYOPIA.label'),
                    value: 'MYOPIA_FOR_PRESBYOPIA',
                },
            ];
        },

        materialOptions()
        {
            const materials = [];

            switch (this.formData.lens_type)
            {
                case 'SOFT':
                    materials.push(
                        'UNISIL blue',
                        'Definitive 74 blue',
                        'Definitive 74 clear',
                        'SIH MT clear',
                        'GM3 58 clear',
                        'GM3 58 blue',
                        'GM3 49 clear',
                        'GM3 49 blue',
                        'IGEL 77 clear',
                        'Contaflex 67 clear',
                        'Igel 58 clear'
                    );
                    break;

                case 'RGP':
                    materials.push(
                        'Optimum Red red',
                        'Optimum Classic blue',
                        'Optimum Classic green',
                        'Contaperm F2 Low clear',
                        'Contaperm F2 Low blue',
                        'Contaperm F2 Low light green',
                        'Contaperm F2 High clear',
                        'Contaperm F2 High blue',
                        'Contaperm F2 High green',
                        'Optimum Comfort blue',
                        'Optimum Comfort green',
                        'Optimum Extra blue',
                        'Optimum Extra green',
                        'Paragon HDS blue',
                        'Paragon HDS green',
                        'Paragon Thin blue',
                        'Paragon Thin green',
                        'BOSTON 4 blue',
                        'BOSTON ES blue',
                        'BOSTON ES ice blue',
                        'BOSTON ES green',
                        'BOSTON EO blue',
                        'BOSTON XO blue',
                        'BOSTON XO green',
                        'BOSTON XO red',
                        'BOSTON XO purple',
                        'INFINITE blue',
                        'INFINITE reg',
                        'INFINITE green'
                    );
                    break;

                case 'SCLERAL':
                    materials.push(
                        'Optimum Extra 16 blue',
                        'Optimum Extra 16 clear',
                        'INFINITE 16 blue',
                        'INFINITE 16 clear'
                    );
                    break;

                case 'ORTHOK':
                    materials.push(
                        'BOSTON XO blue',
                        'BOSTON XO green',
                        'BOSTON XO red',
                        'BOSTON XO purple',
                        'INFINITE blue',
                        'INFINITE red',
                        'INFINITE green'
                    );
                    break;

                default:
                    throw new Error(`Unsupported lens type ${this.formData.lens_type}`);
            }

            return materials;
        },

        guidingEyeOptions()
        {
            return [
                {
                    label: Strings.capitalize(this.$t('common.yes')),
                    value: 'YES',
                },
                {
                    label: Strings.capitalize(this.$t('common.no')),
                    value: 'NO',
                },
            ];
        },

        hasPrototype()
        {
            return ['SOFT', 'RGP'].includes(this.formData.lens_type);
        },

        hasUnifocal()
        {
            return [
                'SOFT',
                'RGP',
                'SCLERAL',
            ].includes(this.formData.lens_type);
        },

        hasMultifocal()
        {
            return [
                'SOFT',
                'RGP',
                'SCLERAL',
            ].includes(this.formData.lens_type);
        },

        // hack: This allows to add a  "Myopia Management"  to the "Focal" field
        hasMyopiaManagement()
        {
            return [
                'SOFT',
                'RGP',
            ].includes(this.formData.lens_type);
        },
    },

    created()
    {
        this.formData = this.modelValue;
    },

    methods:
    {
        /**
         * Ensure form consistency upon lens type change.
         *
         * @param {String} lensType The selected lens type.
         */
        lensTypeChanged(lensType)
        {
            // Reset prototype selection
            if (lensType !== null)
            {
                delete this.formData.prototype;
            }

            // Reset/Validate focal & variant
            if (lensType === 'ORTHOK')
            {
                // OrthoK lenses don't have a focal
                delete this.formData.focal;
            }
            else
            {
                // Other lenses don't have a variant
                delete this.formData.variant;
            }

            // Preselect renewal period if there's only one
            if (lensType && RENEWAL_PERIODS[lensType]?.length === 1)
            {
                this.formData.renewal_period = RENEWAL_PERIODS[lensType][0];
            }

            // Preselect default material for current lens type
            if (this.formData.material)
            {
                switch (lensType)
                {
                    case 'SOFT':
                        // TODO: select default material for Soft
                        // this.formData.material = 'XXX';
                        break;

                    case 'RGP':
                        // TODO: select default material for RGP
                        // this.formData.material = 'XXX';
                        break;

                    case 'SCLERAL':
                        // TODO: select default material for Scleral
                        // this.formData.material = 'XXX';
                        break;

                    case 'ORTHOK':
                        // TODO: select default material for OrthoK
                        // this.formData.material = 'XXX';

                        // OrhtoK lenses can't be multifocal
                        this.formData.focal = 'UNIFOCAL';
                        break;
                }
            }
        },

        emitFormData()
        {
            this.$emit('update:modelValue', this.formData);
        },
    },
};
</script>
